import { useCallback, useEffect, useState } from "react";
import { Search, Lightbulb, Lock, Copy, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AssetPlayer from "@/components/assets/AssetPlayer";
import ExportMenu from "@/components/assets/ExportMenu";
import DraftStudio from "@/components/assets/DraftStudio";
import ReviewQueue from "@/components/assets/ReviewQueue";
import { PacksPanel, SubstitutePack } from "@/components/assets/Packs";
import { UseInMission, AddToPack, OwnActions } from "@/components/assets/StudioActions";
import { useAuth } from "@/context/AuthContext";

function Filters({ f, setF, facets }) {
  const { t } = useI18n();
  const Sel = ({ k, label, opts, fmt = (x) => x }) => (
    <select className="nb-input w-auto py-1.5 text-sm" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} aria-label={label} data-testid={`rs-filter-${k}`}>
      <option value="">{label}: {t("al.all")}</option>{opts.map((o) => <option key={o} value={o}>{fmt(o)}</option>)}
    </select>
  );
  return (
    <div className="flex flex-wrap gap-2 mt-5">
      <div className="relative flex-1 min-w-[12rem]"><Search className="w-4 h-4 absolute start-3 top-3 text-gray-500" />
        <input className="nb-input ps-9" dir="auto" maxLength={60} placeholder={t("al.search")} value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="rs-search" /></div>
      <Sel k="lang" label={t("al.language")} opts={facets.target_languages || []} fmt={(x) => x.toUpperCase()} />
      <Sel k="cefr" label={t("al.level")} opts={facets.cefr || []} />
      <Sel k="family" label={t("al.type")} opts={facets.family || []} fmt={(x) => t(`al.fam.${x}`)} />
      <Sel k="eligible" label={t("al.eligibility")} opts={["public", "pro"]} fmt={(x) => t(`al.elig.${x}`)} />
      <label className="nb-tag bg-white text-xs flex items-center gap-1.5"><input type="checkbox" checked={f.mine} onChange={(e) => setF({ ...f, mine: e.target.checked, shared: false })} data-testid="rs-filter-mine" /> {t("al.mine")}</label>
      <label className="nb-tag bg-white text-xs flex items-center gap-1.5"><input type="checkbox" checked={!!f.shared} onChange={(e) => setF({ ...f, shared: e.target.checked, mine: false })} data-testid="rs-filter-shared" /> {t("al.p.schoolShared")}</label>
    </div>
  );
}

function Detail({ id, onClose, classes, collections, onChanged }) {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [lang, setLang] = useState("");
  const [cls, setCls] = useState("");
  const [adapt, setAdapt] = useState(null);
  const fetchD = useCallback(() => api.get(`/resources/assets/${id}`).then((r) => { setD(r.data); setLang((l) => (r.data.asset.target_languages.includes(l) ? l : r.data.asset.target_languages[0])); }).catch(() => toast.error(t("al.failed"))), [id, t]);
  useEffect(() => { if (id) fetchD(); else { setD(null); setAdapt(null); } }, [id, fetchD]);
  const a = d?.asset;
  const assign = async () => {
    if (!cls) return toast.error(t("al.pickClass"));
    try { await api.post(`/resources/assets/${id}/assign`, { class_ids: [cls], language: lang }); toast.success(t("al.assigned")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("al.failed")); }
  };
  const saveCopy = async () => {
    try { await api.post(`/resources/assets/${id}/derive`, { language: lang, title: adapt.title, student_instructions: adapt.instructions }); toast.success(t("al.adapted")); setAdapt(null); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("al.failed")); }
  };
  const tb = a?.teacher;
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto [&>*]:min-w-0 w-[calc(100vw-1rem)] sm:w-full" data-testid="rs-detail">
        {!a ? <DialogTitle className="sr-only">{t("al.studio")}</DialogTitle> : (
          <>
            <DialogHeader className="text-start sm:text-start">
              <DialogTitle className="font-cabinet text-2xl pe-8" dir="auto" data-testid="rs-detail-title">{a.title}</DialogTitle>
              <div className="flex flex-wrap gap-1.5 text-xs mt-1">
                <span className="nb-tag bg-mint">{a.cefr}</span><span className="nb-tag bg-sky">{t(`al.fam.${a.family}`)}</span>
                {a.eligibility.map((e) => <span key={e} className="nb-tag bg-white" data-testid={`rs-elig-${e}`}>{t(`al.elig.${e}`)}</span>)}
                <span className="nb-tag bg-white !normal-case" dir="ltr" data-testid="rs-asset-id">{a.asset_id}{a.source_asset_id ? ` ← ${a.source_asset_id}` : ""}</span>
              </div>
            </DialogHeader>
            <div className="nb-card bg-lavender p-4 text-sm" data-testid="rs-why">
              <div className="font-bold flex items-center gap-2"><Lightbulb className="w-4 h-4" /> {t("al.why")}</div>
              <p className="mt-1" dir="ltr">{d.why.cefr} · {(d.why.skills || []).join(", ")} · {(d.why.modes || []).join(", ")}</p>
              {d.why.cefr_can_do && <p dir="ltr">{d.why.cefr_can_do}</p>}
              {d.why.strand && <p className="text-xs text-gray-600" dir="ltr">{d.why.strand}{d.why.gy25 ? ` · ${d.why.gy25}` : ""}{d.why.evidence ? ` · ${d.why.evidence}` : ""}</p>}
            </div>
            {tb && (
              <div className="nb-card bg-butter p-4 text-sm space-y-1" data-testid="rs-teacher-block">
                <div className="font-bold flex items-center gap-2"><Lock className="w-4 h-4" /> {t("al.teacherOnly")}</div>
                {tb.instructions && <p dir="ltr"><b>{t("al.notes")}:</b> {tb.instructions}</p>}
                {tb.answer_key_note && <p data-testid="rs-answer-key"><b>{t("al.answerKey")}:</b> <bdi>{tb.answer_key_note}</bdi></p>}
                {tb.rubric && <p data-testid="rs-rubric"><b>{t("al.rubric")}:</b> {tb.rubric.join(" · ")}</p>}
              </div>
            )}
            {a.owner_id && <OwnActions a={a} orgId={classes.find((c) => c.org_id)?.org_id} onChanged={() => { fetchD(); onChanged(); }} />}
            <div className="flex flex-wrap gap-2 items-center">
              <select className="nb-input w-auto py-1.5 text-sm" value={lang} onChange={(e) => setLang(e.target.value)} data-testid="rs-lang">
                {a.target_languages.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
              </select>
              <select className="nb-input w-auto py-1.5 text-sm" value={cls} onChange={(e) => setCls(e.target.value)} data-testid="rs-class">
                <option value="">{t("al.pickClass")}</option>{classes.map((c) => <option key={c.class_id} value={c.class_id}>{c.name}</option>)}
              </select>
              <button onClick={assign} disabled={a.status === "draft"} title={a.status === "draft" ? t("al.p.draftNoAssign") : undefined} className="nb-btn-primary py-1.5 px-3 text-sm disabled:opacity-50" data-testid="rs-assign"><Send className="w-4 h-4" /> {t("al.assign")}</button>
              {!a.owner_id && <button onClick={() => setAdapt({ title: a.title, instructions: a.student_instructions || "" })} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="rs-adapt"><Copy className="w-4 h-4" /> {t("al.adapt")}</button>}
            </div>
            <div className="flex flex-wrap gap-2 items-start">
              <UseInMission id={a.asset_id} lang={lang} />
              <AddToPack id={a.asset_id} collections={collections} onAdded={onChanged} />
            </div>
            <div><div className="text-xs font-bold text-gray-500 mb-1">{t("al.p.export")}</div><ExportMenu base={`/resources/assets/${a.asset_id}/export`} teacher lang={lang} name={a.asset_id} /></div>
            {adapt && (
              <div className="nb-card p-3 space-y-2" data-testid="rs-adapt-form">
                <input className="nb-input" dir="auto" maxLength={120} value={adapt.title} onChange={(e) => setAdapt({ ...adapt, title: e.target.value })} aria-label={t("al.titleLabel")} data-testid="rs-adapt-title" />
                <textarea className="nb-input" dir="auto" maxLength={600} rows={2} value={adapt.instructions} onChange={(e) => setAdapt({ ...adapt, instructions: e.target.value })} aria-label={t("al.instructionsLabel")} data-testid="rs-adapt-instructions" />
                <div className="flex gap-2"><button onClick={saveCopy} className="nb-btn-primary py-1.5 px-3 text-sm" data-testid="rs-adapt-save">{t("al.save")}</button>
                  <button onClick={() => setAdapt(null)} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="rs-adapt-cancel">{t("al.cancel")}</button></div>
              </div>
            )}
            <div className="border-t-2 border-ink/10 pt-3">
              <div className="text-xs font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500 mb-2">{t("al.preview")}</div>
              <AssetPlayer key={lang} family={a.family} language={lang} content={a.renders[lang]} answers={a.renders[lang]?.answers} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const TABS = ["library", "create", "packs", "substitute"];

export default function ResourceStudio() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [tab, setTab] = useState("library");
  const [f, setF] = useState({ q: "", lang: "", cefr: "", family: "", eligible: "", mine: false });
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [open, setOpen] = useState(null);
  const [classes, setClasses] = useState([]);
  const [collections, setCollections] = useState([]);
  const load = useCallback(() => {
    const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/resources/assets", { params: p }).then((r) => setData(r.data)).catch((e) => e?.response?.status === 403 ? setDenied(true) : toast.error(t("al.failed")));
  }, [f, t]);
  const loadCols = useCallback(() => api.get("/library/collections").then((r) => setCollections(r.data.collections)).catch(() => {}), []);
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data.active || [])).catch(() => {}); loadCols(); }, [loadCols]);
  const tabs = user?.is_admin ? [...TABS, "review"] : TABS;
  return (
    <AppLayout>
      <Seo title="Resource Studio" noindex />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="resource-studio">
        <h1 className="font-cabinet font-extrabold text-4xl sm:text-5xl">{t("al.studio")}</h1>
        {denied ? <p className="mt-4 text-coral" data-testid="rs-denied">{t("al.p.denied")}</p> : (
          <>
            <p className="text-gray-700 mt-2">{t("al.studioSub", { n: data?.total ?? "…" })}</p>
            <p className="text-xs text-gray-500 mt-1">{t("al.previewNote")}</p>
            <div className="flex flex-wrap gap-1 mt-4" role="tablist">{tabs.map((k) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={`nb-tag text-sm ${tab === k ? "bg-ink text-white" : "bg-white"}`} data-testid={`rs-tab-${k}`}>{t(`al.p.tab.${k}`)}</button>))}</div>
            {tab === "library" && (
              <>
                <Filters f={f} setF={setF} facets={data?.facets || {}} />
                {data && data.items.length === 0 && <p className="mt-6 text-sm text-gray-500" data-testid="rs-empty">{t("al.noResults")}</p>}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5" data-testid="rs-grid">
                  {data?.items.map((a) => (
                    <button key={a.asset_id} onClick={() => setOpen(a.asset_id)} className="nb-card bg-white p-4 text-start hover:-translate-y-0.5 transition-transform" data-testid={`rs-card-${a.asset_id}`}>
                      <div className="flex flex-wrap gap-1.5 text-[11px]"><span className="nb-tag bg-mint">{a.cefr}</span><span className="nb-tag bg-sky">{t(`al.fam.${a.family}`)}</span>
                        {a.owner_id && <span className="nb-tag bg-butter" data-testid={`rs-status-${a.asset_id}`}>{t(`al.p.st.${a.status}`)}</span>}
                        {a.eligibility.filter((e) => e !== "education").map((e) => <span key={e} className="nb-tag bg-white">{t(`al.elig.${e}`)}</span>)}</div>
                      <div className="font-bold mt-2" dir="auto">{a.title}</div>
                      <div className="text-xs text-gray-500 mt-1" dir="ltr">{a.target_languages.join(" · ").toUpperCase()} · {t("al.minutes", { n: a.duration_min })}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
            {tab === "create" && <DraftStudio onSaved={(x) => { setF({ ...f, mine: true }); setTab("library"); setOpen(x.asset_id); }} />}
            {tab === "packs" && <PacksPanel collections={collections} reload={loadCols} onOpenAsset={setOpen} />}
            {tab === "substitute" && <SubstitutePack languages={data?.facets?.target_languages || ["fr"]} onOpenAsset={setOpen} reload={loadCols} />}
            {tab === "review" && <ReviewQueue />}
          </>
        )}
      </div>
      <Detail id={open} onClose={() => setOpen(null)} classes={classes} collections={collections} onChanged={() => { load(); loadCols(); }} />
    </AppLayout>
  );
}
